import { useNavigate, useSearch } from "@tanstack/react-router";

import { carryFilters } from "@/lib/filters";

import { Chip, FlagChip, LinkChip } from "../../chips";
import { readShared } from "../../filters";
import { OptionLink, TabChip } from "../../links";
import { useTabQuery } from "../../PageFrame";
import type { SourcingTab } from "../../routes";
import { useFilterOptions } from "../../SharedFilterBar";
import { dateGB, Footnote, money, MONTHS, MUTED_ROW } from "../kit";
import { priceDp, SOURCE_LABEL, Tile } from "../margin_at_stake/Page";
import { activityParams, readWindow, windowLabel, windowSearch, type WindowKey } from "./Summary";
import { TopN } from "./TopN";

/** C1 rev 2.0 `item-quotes` (revision 2): one object for one item, never paginated. */
export interface QuoteHeader {
  current_gbp: number | null;
  current_as_of: string | null;
  current_source: string | null;
  annual_quantity: number | null;
  unit_basis: string | null;
  drive_folder: string | null;
  enquiries_sent: number | null;
  suppliers_replied: number | null;
  enquiry_note: string | null;
  facts_as_of: string | null;
}

export interface Purchase {
  variant: string;
  supplier: string;
  price_gbp: number | null;
  purchase_date: string | null;
}

export interface QuoteRow {
  option_id: string;
  option_name: string;
  dropped: boolean;
  origin_country: string | null;
  uk_gbp: number | null;
  uk_incoterm: string | null;
  uk_destination_stated: boolean | null;
  uk_native: number | null;
  uk_currency: string | null;
  eu_gbp: number | null;
  eu_incoterm: string | null;
  eu_native: number | null;
  eu_currency: string | null;
  tiers: number[];
  lead_times: string[];
  valid_until: string | null;
  latest_quote_date: string | null;
  quote_age_days: number | null;
  quote_expired: boolean;
  lines_n: number;
  drive_folder: string | null;
}

/** The size's target (Ioan 2026-10-09): the last price paid for it, else the Sourcing Spec target of an item never bought. */
export interface TableTarget {
  gbp: number;
  source: "latest_purchase" | "manual";
  as_of: string | null;
  supplier: string | null;
}

export interface QuoteTable {
  name: string;
  options_n: number;
  target: TableTarget | null;
  /** This size's yearly volume (Ioan 2026-10-12); the item's when the size has none of its own. */
  annual_quantity: number | null;
  options: QuoteRow[];
}

export interface QuoteLine {
  name: string;
  destination: string | null;
  quantity: number | null;
  gbp: number;
  native: number | null;
  currency: string | null;
  incoterm: string | null;
}

export interface FullQuote {
  option_id: string;
  option_name: string;
  dropped: boolean;
  origin_country: string | null;
  drive_folder: string | null;
  contact_name: string | null;
  contact_email: string | null;
  quote_ref: string | null;
  includes: string | null;
  extra_fees: string | null;
  enquiry_at: string | null;
  quote_at: string | null;
  days_to_quote: number | null;
  lines: QuoteLine[];
}

export interface ItemQuotes {
  item_id: string;
  item_name: string | null;
  funnel: string | null;
  win: WindowKey;
  since: string | null;
  header: QuoteHeader | null;
  purchases: Purchase[];
  tables: QuoteTable[];
  quotes_in_full: FullQuote[];
}

/** The item page's quotes call: part of Item Overview, so not one of the registered tabs. */
export const QUOTES_TAB: SourcingTab = {
  tabKey: "item_overview",
  apiTab: "item-quotes",
  label: "Item Overview",
  group: "HOME",
  needs: "item",
  accepts: ["item", "funnel", "phase", "option", "geo", "dropped"],
  ignoresDropped: false,
};

export const QUOTE_COLUMNS = [
  "#", "Supplier", "UK · Newark", "Incoterm", "EU · Gorzów", "Incoterm", "Saving per year", "Best vs target", "As quoted",
  "Quantity priced", "Lead time", "Valid until", "Drive",
] as const;
export const FULL_COLUMNS = [
  "Supplier and documents", "Contact", "Enquiry to quote", "What the quote includes", "All prices quoted",
] as const;
export const NOT_LOADED = "Loaded when the Supplier Quotes PDF is rebuilt";
export const NO_FOLDER = "No Drive folder";
export const NO_FOLDER_TITLE = "Add the link to the Drive folder property of the page in Notion";
const SYMBOL: Record<string, string> = { GBP: "£", EUR: "€", USD: "$" };
const NUM_COLUMNS = new Set([0, 2, 4, 6, 7, 9]);

/** "per kg" → "kg", "per unit" → "units"; nothing when the basis is unknown. */
export function unitLabel(basis: string | null): string {
  if (!basis) return "";
  const unit = basis.replace(/^per\s+/i, "").trim();
  return unit === "unit" ? "units" : unit;
}

/** One number of decimals for the whole item, from its largest price (as Margin at Stake). */
export function itemDp(data: ItemQuotes): number {
  const prices = data.tables.flatMap((t) => t.options.flatMap((o) => [o.uk_gbp ?? 0, o.eu_gbp ?? 0]));
  const targets = data.tables.map((t) => t.target?.gbp ?? 0);
  return priceDp(Math.max(0, data.header?.current_gbp ?? 0, ...targets, ...prices));
}

/** The price as the supplier wrote it: "In GBP", or "€1.27 EXW" per destination, without repeats. */
export function asQuoted(row: QuoteRow): string {
  const sides: Array<[number | null, string | null, string | null]> = [
    [row.uk_native, row.uk_currency, row.uk_incoterm],
    [row.eu_native, row.eu_currency, row.eu_incoterm],
  ];
  const texts = sides
    .filter(([native, currency]) => native !== null && currency !== null)
    .map(([native, currency, incoterm]) => {
      if (currency === "GBP") return "In GBP";
      const value = native as number;
      const amount = value >= 20 ? value.toFixed(0) : value.toFixed(2);
      return `${SYMBOL[currency as string] ?? `${currency} `}${amount}${incoterm ? ` ${incoterm}` : ""}`;
    });
  return [...new Set(texts)].join(" · ");
}

/** A quantity priced far from what we buy: over three times, or under a tenth, of the yearly volume. */
export function farTier(tiers: number[], volume: number | null): boolean {
  return volume !== null && volume > 0 && tiers.some((t) => t > volume * 3 || t < volume / 10);
}

export function tierText(tiers: number[], unit: string): string {
  const text = tiers.map((t) => t.toLocaleString("en-GB")).join(" · ");
  return unit ? `${text} ${unit}` : text;
}

export function farText(tiers: number[], volume: number): string {
  return `Priced at ${Math.max(...tiers).toLocaleString("en-GB")}, we buy ${volume.toLocaleString("en-GB")} a year`;
}

export function tableTitle(name: string, basis: string | null): string {
  return `${name}: ranked on the lowest quoted price${basis ? ` ${basis}` : ""}`;
}

/** "C8 - COCONUT - Organic" → "Organic"; "DE WIT (NL) - C8 - COCONUT - Organic" → "DE WIT (NL)". */
export const shortVariant = (variant: string): string => variant.replace(/^.* - /, "");
export const supplierName = (supplier: string): string => supplier.split(" - ")[0].trim();

/** An option's lowest price over both destinations. */
export const bestOf = (row: QuoteRow): number => Math.min(...[row.uk_gbp, row.eu_gbp].filter((p): p is number => p !== null));

/** The incoterm of the option's lowest price (UK when both destinations tie). */
export function bestIncoterm(row: QuoteRow): string | null {
  const best = bestOf(row);
  return row.uk_gbp === best ? row.uk_incoterm : row.eu_incoterm;
}

/** Incoterms that deliver to our door; any other stated incoterm leaves delivery to us. */
export const DELIVERED = new Set(["DDP", "DAP", "DPU"]);

/** The yearly saving of picking this option (Ioan 2026-10-12): (target − lowest price) × the size's yearly volume. */
export function savingPerYear(best: number, target: number | null, volume: number | null): number | null {
  return target !== null && volume !== null ? (target - best) * volume : null;
}

/** "+£2,070" saves, "−£1,234" costs more, "£0" neither. */
export function savingText(saving: number): string {
  const rounded = Math.round(saving);
  if (rounded === 0) return money(0, 0);
  return `${rounded > 0 ? "+" : "−"}${money(Math.abs(rounded), 0)}`;
}

/** "£0.0645 below" / "£0.1200 above" / "At target" against the size's target. */
export function vsTarget(best: number, target: number, dp: number): string {
  const gap = Math.round((best - target) * 10 ** dp) / 10 ** dp;
  if (gap === 0) return "At target";
  return `${money(Math.abs(gap), dp)} ${gap < 0 ? "below" : "above"}`;
}

/** "last paid to OPM on 3 Feb 2026" / "the Sourcing Spec target": where the size's target comes from. */
export function targetSource(target: TableTarget): string {
  if (target.source === "manual") return "the Sourcing Spec target";
  const who = target.supplier ? ` to ${supplierName(target.supplier)}` : "";
  return `last paid${who}${target.as_of ? ` on ${dateGB(target.as_of)}` : ""}`;
}

/** Where the dashed target line goes: after the last option priced below the target. */
export function targetIndex(options: QuoteRow[], target: number): number {
  return options.filter((o) => bestOf(o) < target).length;
}

/** "24 Sep" from a timestamp or date, read from its date part. */
export function dayMonth(iso: string): string {
  return `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]}`;
}

export const days = (value: number): string => `${value.toFixed(1)} days`;

export function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export const pct = (part: number, whole: number): string => `${Math.round((part / whole) * 100)}%`;

/** "Organic · UK, 1,000 kg: £4.78 ($6.34) EXW", as the PDF's "All prices quoted". */
export function lineText(line: QuoteLine, unit: string, dp: number): string {
  const where = line.destination ?? "UK, destination not stated";
  const qty = line.quantity !== null ? `, ${line.quantity.toLocaleString("en-GB")}${unit ? ` ${unit}` : ""}` : "";
  const native = line.native !== null && line.currency && line.currency !== "GBP"
    ? ` (${SYMBOL[line.currency] ?? `${line.currency} `}${line.native.toFixed(2)})`
    : "";
  return `${shortVariant(line.name)} · ${where}${qty}: ${money(line.gbp, dp)}${native}${line.incoterm ? ` ${line.incoterm}` : ""}`;
}

function useItemQuotes(): ItemQuotes | null {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const query = useTabQuery<ItemQuotes>(QUOTES_TAB, activityParams(readWindow(search)));
  return query.isSuccess && query.data.meta.health.status !== "failed" ? query.data.data : null;
}

/** The enquiry round, as the PDF's top row: enquiries, replies, quotes, cheapest, fastest. */
export function RoundTiles({ data }: { data: ItemQuotes | null }) {
  const header = data?.header ?? null;
  const known = data !== null;
  const sent = header?.enquiries_sent ?? null;
  const replied = header?.suppliers_replied ?? null;
  const full = data?.quotes_in_full ?? [];
  const lines = full.reduce((n, q) => n + q.lines.length, 0);
  const dp = data ? itemDp(data) : 2;
  const basis = header?.unit_basis ?? null;
  const cheapest = full[0] ?? null;
  const cheapLine = cheapest ? [...cheapest.lines].sort((a, b) => a.gbp - b.gbp)[0] : null;
  const timed = full.filter((q) => q.days_to_quote !== null);
  const fastest = timed.length ? [...timed].sort((a, b) => (a.days_to_quote as number) - (b.days_to_quote as number))[0] : null;
  const typical = median(timed.map((q) => q.days_to_quote as number));
  return (
    <div className="mb-2.5 grid grid-cols-2 gap-2.5 md:grid-cols-5">
      <Tile label="Enquiries sent" value={sent ?? "—"} sub={!known ? null : sent !== null ? "RFQ emails sent" : NOT_LOADED} />
      <Tile
        label="Suppliers replied"
        value={replied ?? "—"}
        sub={!known ? null : replied !== null && sent ? `${pct(replied, sent)} of enquiries` : replied !== null ? null : NOT_LOADED}
      />
      <Tile
        label="Quotes received"
        value={known ? full.length : "—"}
        sub={!known ? null : `${lines.toLocaleString("en-GB")} price lines${sent ? ` · ${pct(full.length, sent)} of enquiries` : ""}`}
      />
      <Tile
        label="Cheapest"
        value={cheapest ? <span className="text-base">{supplierName(cheapest.option_name)}</span> : "—"}
        sub={!known ? null : cheapLine ? `${money(cheapLine.gbp, dp)}${basis ? ` ${basis}` : ""}${cheapLine.incoterm ? `, ${cheapLine.incoterm}` : ""}` : "No quotes yet"}
      />
      <Tile
        label="Fastest quote"
        value={fastest ? <span className="text-base">{supplierName(fastest.option_name)}</span> : "—"}
        sub={!known ? null : fastest
          ? `${days(fastest.days_to_quote as number)} from enquiry${typical !== null ? ` · median ${days(typical)}` : ""}`
          : NOT_LOADED}
      />
    </div>
  );
}

/**
 * Where Offers goes: an ingredients item opens the Ingredients Funnel (on its variant picker while the variant
 * list loads), unless it turns out to have no variants; anything else opens the Sourcing Funnel.
 */
export function offersTarget(funnel: string | null, variants: string[] | null): "ingredients_funnel" | "sourcing_funnel" {
  return funnel === "ingredients" && (variants === null || variants.length > 0) ? "ingredients_funnel" : "sourcing_funnel";
}

/**
 * The Ingredients Funnel's search from Item Overview: the filters as they are, on the variant already picked
 * if the item has it, else its first (Ioan 2026-10-16). With no variant list yet, no variant.
 */
export function offersSearch(previous: Record<string, unknown>, variants: string[] | null): Record<string, unknown> {
  const next = carryFilters(previous);
  const picked = typeof previous.f_variant === "string" && variants?.includes(previous.f_variant);
  if (!picked) {
    if (variants?.length) next.f_variant = variants[0];
    else delete next.f_variant;
  }
  return next;
}

/** The pages Item Overview opens on for the item, in Ioan's order (2026-10-16); Offers is added after. */
export const NAV_TABS = [
  { tabKey: "criteria_ranking", label: "Criteria Ranking" },
  { tabKey: "feature_ranking", label: "Feature Ranking" },
  { tabKey: "ranking_table", label: "Full ranking" },
] as const;

/** Buttons to the item's rankings and offers; each keeps the filters and leaves a trail back here. */
export function NavChips({ funnel, variants }: { funnel: string | null; variants: string[] | null }) {
  const offers = offersTarget(funnel, variants);
  return (
    <>
      {NAV_TABS.map((tab) => (
        <TabChip key={tab.tabKey} tabKey={tab.tabKey}>{tab.label}</TabChip>
      ))}
      <TabChip
        tabKey={offers}
        search={offers === "ingredients_funnel" ? (previous) => offersSearch(previous, variants) : undefined}
      >
        Offers
      </TabChip>
    </>
  );
}

/** What we pay today: current cost, last purchase, volume, target price per size (Ioan 2026-10-09), yearly cost at today's cost. */
export function HeaderTiles({ data, variants = null }: { data: ItemQuotes | null; variants?: string[] | null }) {
  const header = data?.header ?? null;
  const dp = data ? itemDp(data) : 2;
  const basis = header?.unit_basis ?? null;
  const known = data !== null;
  const current = header?.current_gbp ?? null;
  const volume = header?.annual_quantity ?? null;
  const bought = data?.purchases ?? [];
  const targets = (data?.tables ?? []).filter((t): t is QuoteTable & { target: TableTarget } => t.target !== null);
  return (
    <>
      <div className="mb-2.5 flex flex-wrap gap-2">
        <LinkChip href={header?.drive_folder ?? null} label="Open Drive folder" missingLabel={NO_FOLDER} missingTitle={NO_FOLDER_TITLE} />
        <NavChips funnel={data?.funnel ?? null} variants={variants} />
      </div>
      <RoundTiles data={data} />
      <div className="mb-2.5 grid grid-cols-2 gap-2.5 md:grid-cols-5">
        <Tile
          label={basis ? `Current cost ${basis}` : "Current cost"}
          value={current !== null ? money(current, dp) : "—"}
          sub={!known ? null : current !== null && header?.current_as_of
            ? `${SOURCE_LABEL[header.current_source as keyof typeof SOURCE_LABEL] ?? "Current cost"}, as of ${dateGB(header.current_as_of)} · the target to beat`
            : "No current cost yet"}
        />
        <Tile
          label="Last purchase"
          value={bought.length ? "" : "—"}
          sub={!known ? null : bought.length ? (
            <TopN rows={bought}>
              {(shown) => shown.map((p) => (
                <span key={p.variant} className="block">
                  {shortVariant(p.variant)}: <b>{money(p.price_gbp, dp)}</b> {supplierName(p.supplier)}
                  {p.purchase_date ? ` · ${dateGB(p.purchase_date)}` : ""}
                </span>
              ))}
            </TopN>
          ) : "No purchase recorded"}
        />
        <Tile
          label="Our yearly volume"
          value={volume !== null ? volume.toLocaleString("en-GB") : "—"}
          sub={!known ? null : volume !== null ? unitLabel(basis) : "No yearly volume yet"}
        />
        <Tile
          label={basis ? `Target price ${basis}` : "Target price"}
          value={targets.length === 1 ? money(targets[0].target.gbp, dp) : targets.length ? "" : "—"}
          sub={!known ? null : targets.length === 1 ? `Below ${targetSource(targets[0].target)}` : targets.length ? (
            <TopN rows={targets}>
              {(shown) => shown.map((t) => (
                <span key={t.name} className="block">
                  {shortVariant(t.name)}: <b>{money(t.target.gbp, dp)}</b>
                </span>
              ))}
            </TopN>
          ) : "Set by the last purchase or the Sourcing Spec"}
        />
        <Tile
          label="Yearly cost today"
          value={current !== null && volume !== null ? money(current * volume, 0) : "—"}
          sub={!known ? null : current !== null && volume !== null ? "Current cost × our yearly volume" : "Needs the current cost and yearly volume"}
        />
      </div>
    </>
  );
}

const TH = "border-b border-ks-grey-3 px-2 py-1.5 text-left font-bold";
const TD = "border-b border-ks-grey-4 px-2 py-1.5 align-top";
const NUM = "text-right tabular-nums";

function MatchesSpecChip() {
  return (
    <span
      title="Coming in phase 2: does the quote match the item spec?"
      className="ml-1 inline-flex rounded-full border border-dashed border-ks-grey-2 bg-ks-grey-4 px-2 py-0.5 text-xs text-ks-grey-1"
    >
      Matches spec?
    </span>
  );
}

function Price({ gbp, dp, unstated, target }: { gbp: number | null; dp: number; unstated?: boolean; target?: number | null }) {
  if (gbp === null) return <Chip tone="muted">Not quoted</Chip>;
  return (
    <>
      <b className={target != null && gbp < target ? "text-ks-pass" : undefined}>{money(gbp, dp)}</b>
      {unstated ? (
        <span className="block">
          <Chip tone="muted" title="The quote does not say where it delivers to">Destination not stated</Chip>
        </span>
      ) : null}
    </>
  );
}

function TargetLine({ target, dp, basis }: { target: TableTarget; dp: number; basis: string | null }) {
  return (
    <tr>
      <td colSpan={QUOTE_COLUMNS.length} className="border-y-2 border-dashed border-ks-danger px-2 py-1 text-xs text-ks-danger">
        Target: below {money(target.gbp, dp)}{basis ? ` ${basis}` : ""}, {targetSource(target)}. Above this line: under target.
        Below this line: over target.
      </td>
    </tr>
  );
}

/** `shown` rows of the table (the first 10 until "Show all"); the target line is drawn only between shown rows. */
export function QuoteRows({ table, itemId, dp, volume, unit, basis, shown }: {
  table: QuoteTable; itemId: string; dp: number; volume: number | null; unit: string; basis: string | null; shown?: QuoteRow[];
}) {
  const goal = table.target;
  const target = goal?.gbp ?? null;
  const line = target !== null ? targetIndex(table.options, target) : -1;
  const rows = shown ?? table.options;
  return (
    <>
      {rows.map((o, index) => {
        const best = bestOf(o);
        const saving = savingPerYear(best, target, table.annual_quantity);
        const incoterm = bestIncoterm(o);
        return [
          index === line && goal ? <TargetLine key="target" target={goal} dp={dp} basis={basis} /> : null,
          <tr key={o.option_id} className={o.dropped ? MUTED_ROW : ""}>
            <td className={`${TD} ${NUM}`}>{index + 1}</td>
            <td className={TD}>
              <OptionLink optionId={o.option_id} itemId={itemId} from="item_overview">{o.option_name}</OptionLink>
              <MatchesSpecChip />
              {o.quote_expired ? (
                <> <Chip tone="warn" title="Ingredient quotes must be refreshed every 90 days">Quote expired · {o.quote_age_days} days</Chip></>
              ) : null}
              {o.dropped ? <> <FlagChip kind="dropped" /></> : null}
              {o.origin_country ? <span className="block text-xs text-ks-grey-1">{o.origin_country}</span> : null}
            </td>
            <td className={`${TD} ${NUM}`}><Price gbp={o.uk_gbp} dp={dp} unstated={o.uk_gbp !== null && o.uk_destination_stated === false} target={target} /></td>
            <td className={TD}>{o.uk_gbp !== null ? o.uk_incoterm ?? "" : ""}</td>
            <td className={`${TD} ${NUM}`}><Price gbp={o.eu_gbp} dp={dp} target={target} /></td>
            <td className={TD}>{o.eu_gbp !== null ? o.eu_incoterm ?? "" : ""}</td>
            <td className={`${TD} ${NUM} ${saving !== null && Math.round(saving) > 0 ? "font-bold text-ks-pass" : ""} ${saving !== null && Math.round(saving) < 0 ? "text-ks-danger" : ""}`}>
              {saving !== null ? savingText(saving) : "—"}
              {saving !== null && incoterm !== null && !DELIVERED.has(incoterm) ? (
                <span className="block">
                  <Chip tone="muted" title={`${incoterm}: the price does not include delivery to us`}>Delivery not included</Chip>
                </span>
              ) : null}
            </td>
            <td className={`${TD} ${NUM} ${target !== null && best > target ? "text-ks-danger" : ""} ${target !== null && best < target ? "font-bold text-ks-pass" : ""}`}>
              {target !== null ? vsTarget(best, target, dp) : "—"}
            </td>
            <td className={TD}>{asQuoted(o)}</td>
            <td className={`${TD} ${NUM}`}>
              {tierText(o.tiers, unit)}
              {farTier(o.tiers, volume) && volume !== null ? (
                <span className="block">
                  <Chip tone="warn" title="The price is for a quantity far from what we buy">{farText(o.tiers, volume)}</Chip>
                </span>
              ) : null}
            </td>
            <td className={TD}>{o.lead_times.length ? o.lead_times.join(" / ") : "Not stated"}</td>
            <td className={TD}>{o.valid_until ? dateGB(o.valid_until) : "Not stated"}</td>
            <td className={TD}>
              <LinkChip href={o.drive_folder} label="Open folder" missingLabel="—" missingTitle={NO_FOLDER_TITLE} />
            </td>
          </tr>,
        ];
      })}
      {line === rows.length && goal ? <TargetLine target={goal} dp={dp} basis={basis} /> : null}
    </>
  );
}

export function NoQuotes({ win }: { win: WindowKey }) {
  const navigate = useNavigate();
  return (
    <div className="mt-3 rounded-md border border-ks-grey-3 p-3.5 text-sm">
      <b>No quotes in {windowLabel(win)}.</b>
      {win !== "all" ? (
        <button
          type="button"
          data-nonmutating
          className="ml-3 text-ks-purple underline-offset-2 hover:underline focus:underline"
          onClick={() =>
            void navigate({ search: ((previous: Record<string, unknown>) => windowSearch(previous, "all")) as never } as never)
          }
        >
          Show all time
        </button>
      ) : null}
    </div>
  );
}

/** "The quotes in full, in ranking order", as the last table of the PDF. */
export function QuotesInFull({ data }: { data: ItemQuotes }) {
  const dp = itemDp(data);
  const basis = data.header?.unit_basis ?? null;
  const unit = unitLabel(basis);
  return (
    <section>
      <h3 className="mb-2 mt-5 text-sm font-bold">The quotes in full, in ranking order</h3>
      <TopN rows={data.quotes_in_full}>
        {(shown) => (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              {FULL_COLUMNS.map((label) => (
                <th key={label} className={TH}>
                  {label}
                  {label === "All prices quoted" && basis ? <span className="block font-normal">in GBP {basis}</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((q) => (
              <tr key={q.option_id} className={q.dropped ? MUTED_ROW : ""}>
                <td className={TD}>
                  <OptionLink optionId={q.option_id} itemId={data.item_id} from="item_overview">{supplierName(q.option_name)}</OptionLink>
                  {q.origin_country ? <span className="block text-xs text-ks-grey-1">{q.origin_country}</span> : null}
                  <span className="mt-1 block">
                    <LinkChip href={q.drive_folder} label="Open Drive folder" missingLabel={NO_FOLDER} missingTitle={NO_FOLDER_TITLE} />
                  </span>
                </td>
                <td className={TD}>
                  {q.contact_name ?? <span className="text-ks-grey-1">Not recorded</span>}
                  {q.contact_email ? <span className="block text-xs text-ks-grey-1">{q.contact_email}</span> : null}
                </td>
                <td className={TD}>
                  {q.enquiry_at && q.quote_at ? (
                    <>
                      Enquiry {dayMonth(q.enquiry_at)} → quote {dayMonth(q.quote_at)}
                      {q.days_to_quote !== null ? <span className="block font-bold">{days(q.days_to_quote)}</span> : null}
                    </>
                  ) : (
                    <span className="text-ks-grey-1">Not recorded</span>
                  )}
                </td>
                <td className={TD}>
                  {q.quote_ref ? <span className="block font-bold">{q.quote_ref}</span> : null}
                  {q.includes ?? (q.quote_ref ? null : <span className="text-ks-grey-1">{NOT_LOADED}</span>)}
                  {q.extra_fees ? <span className="block">Extra costs: {q.extra_fees}</span> : null}
                </td>
                <td className={TD}>
                  {q.lines.map((l, i) => (
                    <span key={i} className="block whitespace-nowrap">{lineText(l, unit, dp)}</span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
          )}
      </TopN>
    </section>
  );
}

export function QuoteTables({ data, win }: { data: ItemQuotes; win: WindowKey }) {
  if (data.tables.length === 0) return <NoQuotes win={win} />;
  const dp = itemDp(data);
  const basis = data.header?.unit_basis ?? null;
  const volume = data.header?.annual_quantity ?? null;
  const unit = unitLabel(basis);
  return (
    <>
      {data.tables.map((table) => (
        <section key={table.name}>
          <h3 className="mb-2 mt-4 text-sm font-bold">{tableTitle(table.name, basis)} and saving per year</h3>
          <TopN rows={table.options}>
            {(shown) => (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {QUOTE_COLUMNS.map((label, index) => (
                    <th key={`${label}-${index}`} className={`${TH} ${NUM_COLUMNS.has(index) ? NUM : ""}`}>
                      {label}
                      {(index === 2 || index === 4) && basis ? <span className="block font-normal">£ {basis}</span> : null}
                      {index === 6 ? <span className="block font-normal">£ at this size's yearly volume</span> : null}
                      {index === 7 && basis ? <span className="block font-normal">£ {basis}</span> : null}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <QuoteRows table={table} itemId={data.item_id} dp={dp} volume={volume} unit={unit} basis={basis} shown={shown} />
              </tbody>
            </table>
          </div>
            )}
          </TopN>
        </section>
      ))}
      <Footnote>
        <b>DDP</b> delivered, duty and tax paid · <b>DAP</b> delivered, duty and tax on us · <b>CIF</b> freight paid to the
        port · <b>FOB</b> loaded at the origin port · <b>FCA</b> handed to our carrier · <b>EXW</b> collected from the
        supplier. Ranked on the quoted price converted to GBP, so the biggest saving comes first. Incoterms differ, so landed
        costs differ. Saving per year = (the size's target − the option's lowest price) × the size's yearly volume: green
        saves, red costs more. Delivery not included: the quote is not delivered to us, so delivery comes on top.
      </Footnote>
      {data.quotes_in_full.length ? <QuotesInFull data={data} /> : null}
    </>
  );
}

/** The item page's header: Drive folder, the enquiry round and what we pay today; "—" until the call answers. */
export function ItemHeaderTiles() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const options = useFilterOptions(readShared(search).item);
  return <HeaderTiles data={useItemQuotes()} variants={options.data?.data?.variants ?? null} />;
}

/** The item page's ranked quotes and the quotes in full, under the window tiles and above the existing blocks. */
export function ItemQuoteTables() {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const data = useItemQuotes();
  return data ? <div className="mb-4"><QuoteTables data={data} win={readWindow(search)} /></div> : null;
}
